import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faNetworkWired, faBolt, faShieldHalved, faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/ITAssetDependency-Process.png";
import vis from "../../assets/images/use-cases/ITAssetDependency-Dashboard.png";

const ITAssetDependencyMapping = () => {
    return (
        <>
            <SEO
                title="IT Asset Dependency Mapping: AI Digital Twin for Infrastructure | Nebulyx AI"
                description="Model your IT estate as a dependency graph to simulate failures and understand cascade effects. Identify single points of failure and prioritise resilience investments offline."
                keywords="AI digital twin, IT asset management, dependency mapping, infrastructure resilience, CMDB, cascade analysis, failure simulation"
                canonicalUrl="https://nebulyx.ai/use-cases/it-asset-dependency-mapping"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faNetworkWired}/>
                                            &nbsp;
                                            IT Operations
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;If this system goes down, what else breaks?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your IT estate as a dependency graph connecting software, hardware, and data center components. Simulate failures offline to understand cascade effects before incidents occur.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faBolt} className="stat-icon"/>
                                <span className="stat-value">73%</span>
                                <span className="stat-label">Cascade Visibility</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faShieldHalved} className="stat-icon"/>
                                <span className="stat-value">12</span>
                                <span className="stat-label">SPOFs Identified</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faLayerGroup} className="stat-icon"/>
                                <span className="stat-value">2,400+</span>
                                <span className="stat-label">Assets Mapped</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Unknown dependencies between IT assets</li>
                                            <li>Incidents cause unexpected cascade failures</li>
                                            <li>CMDB data is incomplete or out of date</li>
                                            <li>No way to test failure scenarios safely</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a graph-based model of your IT estate</li>
                                            <li>Resolve entities across data sources</li>
                                            <li>Overlay impact and blast radius analytics</li>
                                            <li>Simulate failures offline before they happen</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Identify single points of failure proactively</li>
                                            <li>Understand cascade effects before incidents</li>
                                            <li>Prioritise resilience investments with data</li>
                                            <li>Reduce MTTR through pre-planned responses</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect asset data, model dependencies as a graph, overlay impact analytics, then simulate failures to understand blast radius and cascade effects.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>CMDB, monitoring systems, network topology, service catalog</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Estate</strong>
                                    <span>Resolve entities, discover dependencies, build the asset graph</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Blast radius, cascade depth, SPOF detection, service impact scores</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Inject simulated failures, trace cascade paths, generate impact reports</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects CMDB, monitoring, network topology, and service catalog to build a comprehensive dependency graph.
                                            Entity resolution matches assets across sources using hostnames, IPs, and identifiers. Graph analytics calculate
                                            blast radius and cascade depth for every node, enabling offline impact simulation before real incidents occur.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Impact Simulation Workbench</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises the IT estate as an interactive dependency graph with real-time health status.
                                            Users can inject simulated failures at any node to see cascade effects propagate through the infrastructure,
                                            identify affected business services, and generate recovery runbooks-all tested offline before production incidents.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>CMDB Asset Inventory</li>
                                            <li>Hardware & Software Specs</li>
                                            <li>Network Topology Maps</li>
                                            <li>Monitoring & Alert History</li>
                                            <li>Service Catalog & SLAs</li>
                                            <li>Application Config Files</li>
                                            <li>Data Center Locations</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Entity resolution & matching</li>
                                            <li>Dependency inference</li>
                                            <li>Blast radius calculation</li>
                                            <li>Cascade depth prediction</li>
                                            <li>Single point of failure detection</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Single node failure</li>
                                            <li>Data center outage</li>
                                            <li>Network partition</li>
                                            <li>Database failover</li>
                                            <li>Multi-component cascade</li>
                                            <li>Planned maintenance impact</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the IT estate as a dependency graph and running failure simulations offline,
                                        teams could discover that a single load balancer has 47 downstream dependents, making it a critical
                                        single point of failure affecting 3 Tier-1 business services.
                                        Simulation may reveal that <strong>adding redundancy to just 4 assets could reduce blast radius by 60%</strong>,
                                        enabling data-driven prioritisation of resilience investments before incidents occur.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">4 Assets → 60% Risk Reduction</span>
                                        <span className="discovery-note">Targeted redundancy, tested offline</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Understand Your Infrastructure Dependencies?</h2>
                            <p>
                                If cascade failures are catching you off guard and you need visibility into your IT estate,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default ITAssetDependencyMapping;